/**
 * SharedWithUs (/account/inbox)
 *
 * Estimates customers have sent to the partner location(s) this account is
 * attached to (a retailer store or a trusted installer). Opening one follows its
 * private share link; replies happen there.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Inbox, Eye, EyeOff, MessageSquare, Store, HardHat, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchPartnerInbox } from '../services/shareApi';
import type { InboxItem, InboxLocation } from '../types/share';

const formatDate = (s: string | null) => (s ? new Date(s.replace(' ', 'T')).toLocaleDateString() : '');

export const SharedWithUs: React.FC = () => {
  const { t } = useTranslation('account');
  const { user } = useAuth();
  const [locations, setLocations] = useState<InboxLocation[]>([]);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPartnerInbox()
      .then(r => { if (!cancelled) { setLocations(r.locations); setItems(r.shares); } })
      .catch(() => { if (!cancelled) setError(t('inbox.loadFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.sub, t]);

  const multi = locations.length > 1;
  const openHref = (it: InboxItem) => `/estimate/${it.guid}`;

  return (
    <div className="card shadow-sm px-2 px-sm-0">
      <div className="card-body px-0 px-sm-3 py-3 py-sm-4">
        <div className="d-flex align-items-center justify-content-between mb-1 gap-2">
          <h5 className="fw-bold m-0 d-inline-flex align-items-center gap-2"><Inbox size={18} /> {t('inbox.title')}</h5>
        </div>
        <p className="text-muted small mb-3">
          {locations.length === 0 ? t('inbox.noLocations') : t('inbox.description', { locations: locations.map(l => l.name).join(', ') })}
        </p>

        {loading ? (
          <div className="text-center py-4"><Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} /></div>
        ) : error ? (
          <p className="text-danger small mb-0">{error}</p>
        ) : items.length === 0 ? (
          <p className="text-muted small mb-0">{t('inbox.empty')}</p>
        ) : (
          <>
            <div className="d-none d-md-block table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr className="text-muted small">
                    <th>{t('inbox.columns.estimate')}</th>
                    <th>{t('inbox.columns.customer')}</th>
                    {multi && <th>{t('inbox.columns.location')}</th>}
                    <th>{t('inbox.columns.sent')}</th>
                    <th className="text-center" title={t('inbox.columns.opened')}><Eye size={14} /></th>
                    <th className="text-center" title={t('inbox.columns.replies')}><MessageSquare size={14} /></th>
                    <th className="text-end">{t('estimates.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id} className={it.unread > 0 ? 'fw-semibold' : ''}>
                      <td>
                        <div className="small">{it.estimateName || <span className="text-muted fst-italic">{t('estimates.untitled')}</span>}</div>
                        {it.message && <div className="text-muted fw-normal text-truncate" style={{ fontSize: 12, maxWidth: 320 }}>{it.message}</div>}
                      </td>
                      <td className="small">{it.customerName}{(it.customerCity || it.customerRegion) && <div className="text-muted fw-normal" style={{ fontSize: 12 }}>{[it.customerCity, it.customerRegion].filter(Boolean).join(', ')}</div>}</td>
                      {multi && <td className="small"><span className="d-inline-flex align-items-center gap-1">{it.kind === 'retailer' ? <Store size={13} /> : <HardHat size={13} />} {it.locationName}</span></td>}
                      <td className="small text-nowrap">{formatDate(it.sentAt)}</td>
                      <td className="text-center small">{it.firstViewedAt ? <Eye size={14} className="text-success" /> : <EyeOff size={14} className="text-muted" />}</td>
                      <td className="text-center small">
                        <span className={it.commentCount > 0 ? 'text-dark' : 'text-muted'}>{it.commentCount}</span>
                        {it.unread > 0 && <span className="badge text-bg-warning text-dark ms-1">{t('inbox.unread', { count: it.unread })}</span>}
                      </td>
                      <td className="text-end">
                        <a className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" href={openHref(it)}><ExternalLink size={14} /> {t('inbox.open')}</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-md-none d-flex flex-column gap-2">
              {items.map(it => (
                <div key={it.id} className="border rounded-3 p-2">
                  <div className="fw-semibold">{it.estimateName || <span className="text-muted fst-italic">{t('estimates.untitled')}</span>}</div>
                  <div className="text-muted small">{it.customerName}{multi && <> · {it.locationName}</>}</div>
                  <div className="text-muted small d-flex gap-3 mt-1 mb-2">
                    <span>{formatDate(it.sentAt)}</span>
                    <span className="d-inline-flex align-items-center gap-1">{it.firstViewedAt ? <Eye size={14} /> : <EyeOff size={14} />}</span>
                    <span className="d-inline-flex align-items-center gap-1"><MessageSquare size={14} /> {it.commentCount}{it.unread > 0 && <span className="badge text-bg-warning text-dark">{t('inbox.unread', { count: it.unread })}</span>}</span>
                  </div>
                  <a className="btn btn-primary btn-sm w-100" href={openHref(it)}>{t('inbox.open')}</a>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharedWithUs;
